import { JsonSchema, UISchemaElement } from '@jsonforms/core';
import { materialCells } from '@jsonforms/material-renderers';
import { JsonForms } from '@jsonforms/react';
import { useQuery } from '@tanstack/react-query';
import { IntlShape, useIntl } from 'react-intl';
import * as z from 'zod';
import { endpoints, getIDFromUrl, Url } from '@rossum/api-client';
import { HookInterface } from '@rossum/api-client/hookInterfaces';
import { QueueAction } from '@rossum/api-client/schemas';
import { IconLayoutGrid } from '@rossum/ui/icons/tabler';
import { Alert, LinearProgress } from '@rossum/ui/material';
import { useApiClient } from '../../../lib/apiClient';
import { renderers } from '../../extension-interop/custom-controls/renderers';
import { intentSchema } from '../../extension-interop/hooks/intent.schema';
import { createForm } from './createForm';

export const customActionPayloadSchema = (_intl?: IntlShape) =>
  z.object({
    hook_interface: z.string(),
    payload: z.unknown().default({}),
  });

export const useInvokeHookInterfaceQuery = ({
  hookInterface,
}: {
  hookInterface: Url;
}) => {
  const api = useApiClient();

  return useQuery({
    queryKey: ['invoke_hook_interface', hookInterface],
    queryFn: async () => {
      const response = await api.request(
        endpoints.hookInterfaces.invoke(getIDFromUrl(hookInterface), {
          configure: true,
        })
      );
      return z
        .object({
          intent: intentSchema.optional(),
        })
        .parse(response);
    },
  });
};

export const CustomActionForm = createForm(
  ({ value, onChange }) => {
    const intl = useIntl();

    // TODO: This does not work as intended, need someone to help me fix it :sadpanda:
    // const formTheme = useMemo(
    //   () =>
    //     createTheme({
    //       ...theme,
    //       components: {
    //         ...theme.components,
    //         MuiOutlinedInput: {
    //           ...(theme.components?.MuiOutlinedInput ?? {}),
    //           styleOverrides: {
    //             input: {
    //               // No idea why styling via default props does not work
    //               padding: '8.5px 14px',
    //             },
    //           },
    //         },
    //       },
    //     }),
    //   [theme]
    // );

    const {
      data: response,
      isLoading,
      isError,
      error,
    } = useInvokeHookInterfaceQuery({
      hookInterface: value.hook_interface,
    });

    return isLoading ? (
      <LinearProgress />
    ) : isError ? (
      <Alert color="error">{error?.toString()}</Alert>
    ) : !response.intent ? (
      <Alert color="error">
        {intl.formatMessage({
          id: 'features.queueSettings.rules.actions.documentAction.custom.errorIntent',
        })}
      </Alert>
    ) : (
      <>
        {response.intent.error ? (
          <Alert color="error">{response.intent.error.message}</Alert>
        ) : null}

        {response.intent.info ? (
          <Alert color="info">{response.intent.info.message}</Alert>
        ) : null}

        {response.intent.form ? (
          <JsonForms
            schema={response.intent.form.schema as JsonSchema}
            uischema={response.intent.form.uiSchema as UISchemaElement}
            data={value.payload}
            validationMode="NoValidation"
            renderers={renderers}
            cells={materialCells}
            onChange={({ data }) => {
              if (data !== value.payload) {
                onChange({
                  hook_interface: value.hook_interface,
                  payload: data,
                });
              }
            }}
          />
        ) : null}
      </>
    );
  },
  (intl?: IntlShape) => customActionPayloadSchema(intl)
);

export const createCustomAction = (hook_interface: HookInterface) =>
  ({
    icon: IconLayoutGrid,

    resolveAction: (a: QueueAction) => {
      const parseResult = customActionPayloadSchema(undefined).safeParse(
        a.payload
      );
      return (
        a.type === 'custom' &&
        parseResult.success === true &&
        parseResult.data.hook_interface === hook_interface.url
      );
    },

    serialized: {
      type: 'custom' as const,
      payload: {
        hook_interface: hook_interface.url,
        payload: {},
      },
    },

    groupIntlKey: 'other' as const,
    form: CustomActionForm,
    header: null,
    supportedEvents: [
      'validation',
      'annotation_imported',
      'annotation_import_succeeded',
      'annotation_import_failed',
      'annotation_confirmed',
      'annotation_exported',
      'annotation_export_succeeded',
      'annotation_export_failed',
    ] as const,
    payloadSchema: (intl?: IntlShape) => customActionPayloadSchema(intl),
  }) as const;
